import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../../context/AuthContext";
import CustomerDropdown from "../../../../../components/Shared/CustomerDropdown";
import { saveTokens } from "../../../../../services/socialTokenService";
import { CognitoAuthService } from "../../../../../services/cognitoService";
import ConnectionSuccessAlert from "../../../../../components/Shared/ConnectionSuccessAlert";
import { Platform } from "../../../../../types/Platforms";
import { handleInstagramDirectCallback } from "../services/instagramAuthentication";
import { Customer } from "../../../../../types/customer";
import { debug, formatSafeErrorMessage } from "../../../../../utils/debugLogger";
import { validateOAuthState } from "../../../../../utils/oauthState";

interface InstagramDirectState {
  loading: boolean;
  error: string | null;
  accessToken: string | null;
  userId: string | null;
  username: string | null;
  accountType: string | null;
  selectedCustomer: Customer | null;
  connectionSaved: boolean;
  successMessage: string | null;
}

const InstagramDirectCallback: React.FC = () => {
  debug.log("InstagramDirectCallback component mounted");
  const navigate = useNavigate();
  const { isAuthenticated, idToken } = useAuth();
  const [isSaving, setIsSaving] = useState(false);

  const [state, setState] = useState<InstagramDirectState>({
    loading: true,
    error: null,
    accessToken: null,
    userId: null,
    username: null,
    accountType: null,
    selectedCustomer: null,
    connectionSaved: false,
    successMessage: null,
  });

  useEffect(() => {
    const processCallback = async () => {
      try {
        debug.log("Processing Instagram Direct callback...");
        const urlParams = new URLSearchParams(window.location.search);
        if (!validateOAuthState("instagram_direct", urlParams.get("state"))) {
          throw new Error("Security check failed (invalid OAuth state). Please try connecting again.");
        }
        const code = urlParams.get("code");
        const error = urlParams.get("error");

        if (error) {
          throw new Error(`Instagram auth error: ${error}`);
        }

        if (!code) {
          throw new Error("No authorization code received");
        }

        // Handle the callback
        const response = await handleInstagramDirectCallback(code);

        debug.log("Instagram Direct callback successful:", {
          accessToken: !!response.access_token,
          userId: response.user_id,
          username: response.profile.username,
          accountType: response.profile.account_type,
        });

        setState((prev) => ({
          ...prev,
          accessToken: response.access_token,
          userId: response.user_id,
          username: response.profile.username,
          accountType: response.profile.account_type || "personal",
          loading: false,
        }));
      } catch (error: unknown) {
        debug.error(
          "Instagram Direct authentication error:",
          formatSafeErrorMessage(error)
        );
        setState((prev) => ({
          ...prev,
          error: formatSafeErrorMessage(error) || "Failed to connect to Instagram",
          loading: false,
        }));
      }
    };

    processCallback();
  }, []);

  const handleSaveConnection = async () => {
    const { selectedCustomer, accessToken, userId, username } = state;

    if (!selectedCustomer || !accessToken || !userId || !username) {
      setState((prev) => ({
        ...prev,
        error: "Missing required information to save connection",
      }));
      return;
    }

    try {
      setIsSaving(true);

      // Get created_by from Cognito token
      const created_by = CognitoAuthService.getUserEmail(idToken);

      // Update the profile format to include the actual account type
      const profiles = [
        {
          id: userId,
          token: accessToken,
          metadata: {
            name: username,
            category: `Instagram ${
              state.accountType === "BUSINESS"
                ? "Business"
                : state.accountType === "CREATOR"
                ? "Creator"
                : "Personal"
            }`,
            type: state.accountType?.toLowerCase() || "personal",
          },
        },
      ];

      // Use the same saveTokens function as Instagram Business
      await saveTokens(
        {
          customer_id: selectedCustomer.customer_id,
          created_by,
          platform: Platform.INSTAGRAM,
          profiles,
        },
        idToken
      );

      setState((prev) => ({
        ...prev,
        connectionSaved: true,
        successMessage: "Instagram connection saved successfully!",
      }));
    } catch (error: unknown) {
      debug.error(
        "Error saving Instagram connection:",
        formatSafeErrorMessage(error)
      );
      setState((prev) => ({
        ...prev,
        error: "Failed to save Instagram connection",
      }));
    } finally {
      setIsSaving(false);
    }
  };

  if (!isAuthenticated) {
    sessionStorage.setItem("instagram_direct_auth_success", "true");
    navigate("/login", {
      state: { returnUrl: "/social-posts?tab=connect" },
    });
    return null;
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4">
      {state.loading ? (
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-primary mb-4"></div>
          <p>Connecting your Instagram account...</p>
        </div>
      ) : state.error ? (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4 max-w-md">
          <div className="flex">
            <div className="py-1">
              <svg
                className="w-6 h-6 mr-4"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div>
              <p>{state.error}</p>
              <button
                onClick={() => navigate("/social-posts?tab=connect")}
                className="text-sm underline mt-2"
              >
                Return to connections
              </button>
            </div>
          </div>
        </div>
      ) : state.connectionSaved ? (
        <ConnectionSuccessAlert
          message={
            state.successMessage || "Instagram connection saved successfully!"
          }
          onReturnToConnections={() => navigate("/social-posts?tab=connect")}
          onGoHome={() => navigate("/")}
        />
      ) : (
        <div className="w-full max-w-md space-y-4">
          <h2 className="text-2xl font-bold text-center">
            Connect Instagram Account
          </h2>
          // Update the account type label to dynamically show the actual
          account type
          {state.username && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gradient-to-tr from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-lg mr-3">
                  {state.username[0]?.toUpperCase() || "I"}
                </div>
                <div>
                  <h3 className="font-medium">@{state.username}</h3>
                  <p className="text-sm text-gray-600">
                    Instagram{" "}
                    {state.accountType === "BUSINESS"
                      ? "Business"
                      : state.accountType === "CREATOR"
                      ? "Creator"
                      : "Personal"}{" "}
                    Account
                  </p>
                </div>
              </div>
            </div>
          )}
          <CustomerDropdown
            onCustomerSelect={(customer) =>
              setState((prev) => ({ ...prev, selectedCustomer: customer }))
            }
          />
          <div className="mt-2 text-sm text-gray-600">
            <p>
              Please select the business you want to connect this Instagram
              account to.
            </p>
          </div>
          <button
            onClick={handleSaveConnection}
            disabled={!state.selectedCustomer || isSaving}
            className={`
              w-full py-2 px-4 rounded flex items-center justify-center
              ${
                !state.selectedCustomer
                  ? "bg-surface-ui cursor-not-allowed"
                  : "bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:opacity-90"
              }
            `}
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-3"></div>
                <span>Saving Connection...</span>
              </>
            ) : !state.selectedCustomer ? (
              "Select a Business to Continue"
            ) : (
              "Save Connection"
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default InstagramDirectCallback;
